import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, RefreshCw, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";

const iconBtn = "h-9 w-9 inline-flex items-center justify-center border border-obsidian-600 text-bone-400 hover:text-terracotta hover:border-terracotta transition-colors disabled:opacity-40";

export default function PromptAudioRow({ p, onChange, disabled }) {
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const act = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      await fn();
      if (ok) toast.success(ok);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };
  const play = () => act("play", async () => {
    const r = await api.get(`/admin/prompt-audio/${p.prompt_id}/audio`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = new Audio(url);
    a.onended = () => URL.revokeObjectURL(url);
    await a.play();
  });
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("audio", f);
    act("upload", () => api.post(`/admin/prompt-audio/${p.prompt_id}/upload`, fd), "Uploaded.");
  };

  return (
    <li className="flex items-start gap-3 py-3 border-t border-obsidian-700" data-testid={`prompt-audio-row-${p.prompt_id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-bone-200 leading-snug">{p.text}</p>
        <p className="mt-1 font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500" data-testid={`prompt-audio-status-${p.prompt_id}`}>
          {p.has_audio ? (p.source === "uploaded" ? "Uploaded" : "Recorded in your voice") : "Not recorded"}
        </p>
      </div>
      <div className="flex gap-1.5 shrink-0">
        {p.has_audio && (
          <button onClick={play} disabled={!!busy} className={iconBtn} aria-label="Play" data-testid={`prompt-audio-play-${p.prompt_id}`}>
            {busy === "play" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          </button>
        )}
        <button onClick={() => act("gen", () => api.post(`/admin/prompt-audio/${p.prompt_id}/generate`), "Recorded.")} disabled={!!busy || disabled}
          className={iconBtn} aria-label="Record with my voice" title="Record with my voice" data-testid={`prompt-audio-generate-${p.prompt_id}`}>
          {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={!!busy || disabled} className={iconBtn} aria-label="Upload audio" title="Upload audio"
          data-testid={`prompt-audio-upload-${p.prompt_id}`}>
          {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        </button>
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={upload} data-testid={`prompt-audio-file-${p.prompt_id}`} />
        {p.has_audio && (
          <button onClick={() => window.confirm("Remove this recording? Listeners will hear it rendered live instead.") && act("del", () => api.delete(`/admin/prompt-audio/${p.prompt_id}`), "Recording removed.")} disabled={!!busy || disabled}
            className={iconBtn} aria-label="Remove" data-testid={`prompt-audio-delete-${p.prompt_id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}
